/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Flex,
  Icon,
  Text,
  VStack,
  Box,
} from '@chakra-ui/react';
import CheckIcon from '@/assets/svg/check-stroke.svg?react';
import ImageUpload from '@/components/common/ImageUpload';
import Input from '@/components/common/Input';
import Button from '@/components/common/Button';
import { useState } from 'react';

interface MaintenanceConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (file: File, comment: string) => Promise<void> | void;
  title?: string;
  description: string;
  imageLabel: string;
  targetStatus: string;
  orderId?: string;
  isLoading?: boolean;
  isDisabled?: boolean;
}

const MaintenanceConfirmationModal: React.FC<MaintenanceConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'Confirm Status Change',
  description,
  imageLabel,
  targetStatus,
  isLoading = false,
  isDisabled = false,
}) => {
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [comment, setComment] = useState('');
  const [showImageError, setShowImageError] = useState(false);
  const [internalLoading, setInternalLoading] = useState(false);

  const effectiveLoading = isLoading || internalLoading;

  const handleClose = () => {
    setUploadedFile(null);
    setComment('');
    setShowImageError(false);
    setInternalLoading(false);
    onClose();
  };

  const handleConfirmClick = async () => {
    if (!uploadedFile) {
      setShowImageError(true);
      return;
    }

    setShowImageError(false);
    setInternalLoading(true);

    try {
      await onConfirm(uploadedFile, comment);
    } finally {
      setInternalLoading(false);
    }

    handleClose();
  };

  const handleFileChange = (file: File | null) => {
    setUploadedFile(file);
    setShowImageError(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontFamily={'primary'}>
          <Flex align="center" gap={3}>
            <Icon as={CheckIcon} color="brand.primary.DEFAULT" />
            {title}
          </Flex>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack spacing={5} align="stretch">
            <Text>{description}</Text>

            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={2} color="gray.700">
                {imageLabel}{' '}
                <Text as="span" color="red.500">
                  *
                </Text>
              </Text>
              <ImageUpload label="" onChange={handleFileChange} initialImage={null} required />
              {showImageError && (
                <Text fontSize="xs" color="red.500" mt={1}>
                  Please upload an image to confirm {targetStatus.toLowerCase()}.
                </Text>
              )}
            </Box>

            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={2} color="gray.700">
                Comment (Optional)
              </Text>
              <Input
                as="textarea"
                placeholder="Add any notes about the maintenance"
                minHeight="100px"
                resize="vertical"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                borderColor="gray.300"
                _hover={{ borderColor: 'gray.400' }}
                _focus={{
                  borderColor: 'brand.primary.DEFAULT',
                  boxShadow: '0 0 0 1px var(--chakra-colors-brand-primary-DEFAULT)',
                }}
              />
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="outline" onClick={handleClose} isDisabled={effectiveLoading}>
            Cancel
          </Button>

          <Button
            bg="brand.primary.DEFAULT"
            color="white"
            _hover={{ bg: 'brand.primary.700' }}
            _active={{ bg: 'brand.primary.800' }}
            py="10px"
            px={4}
            borderRadius="xl"
            fontFamily="primary"
            boxShadow="0px 1px 2px 0px #1018280D, 0px -2px 0px 0px #1018280D inset, 0px 0px 0px 1px #1018282E inset"
            isLoading={effectiveLoading}
            isDisabled={effectiveLoading || isDisabled || !uploadedFile}
            onClick={handleConfirmClick}
            ml={3}
          >
            {effectiveLoading ? 'Processing...' : 'Confirm & Move Status'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default MaintenanceConfirmationModal;
